import React from 'react';
import PropTypes from 'prop-types';
import { CardText } from 'react-toolbox/lib/card';
import Dropdown from 'react-toolbox/lib/dropdown';
import Form, {
  formatPhoneNumber,
  scrubNumber,
} from '../../../../../common/components/Form';
import ThemedInput from '../../../../../common/components/ThemedInput';
import { normalizePropertyPath } from '../../../../../common/utils/property-path';
import OptionLists from '../../../../../common/option-lists';
import { fillWithDefaults } from '../../../utils/defaultDataHelper';
import { buildAddress } from '../index.js';
import styles from '../MembershipInfo.css';


const optionsToDropDown = optionArr =>
  optionArr.map(value => {
    const cleanedValue = value.replace(/\s+/g, '');
    return {
      value: cleanedValue,
      label: value,
    };
  });


const getPhonePathByType = (form, type) => {
  const { contactDetails } = form || {};
  const numbers = contactDetails ? contactDetails.phoneNumbers || [] : [];
  const phoneIndex = numbers.findIndex(
    number => number.type && number.type.toLowerCase() === type.toLowerCase()
  );
  return `contactDetails.phoneNumbers[${phoneIndex}].number`;
};

export default class PersonalEdit extends React.Component {
  static contextTypes = {
    activateServiceDialog: PropTypes.func,
  };
  static propTypes = {
    currentCustomer: PropTypes.shape({
      personalDetails: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        prefix: PropTypes.string,
      }),
      personalAddress: PropTypes.shape({
        addressLine1: PropTypes.string,
        addressLine2: PropTypes.string,
        city: PropTypes.string,
        stateCode: PropTypes.string,
        postalCode: PropTypes.string,
      }),
      contactDetails: PropTypes.shape({
        phoneNumbers: PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.string,
            number: PropTypes.string,
          })
        ),
      }),
    }),
    actions: PropTypes.shape({
      customer: PropTypes.objectOf(PropTypes.func),
    }),
  };
  formInputs = [
    {
      Component: Dropdown,
      name: 'personalDetails.prefix',
      auto: true,
      source: optionsToDropDown(
        OptionLists.PrefixOptions.filter(option => option !== 'None')
      ),
      key: 'dd-prefix',
      icon: 'face',
      className: styles.themedDropdown,
      theme: styles,
      required: true,
    },
    {
      Component: ThemedInput,
      name: 'personalDetails.firstName',
      label: 'First Name',
      type: 'text',
      pattern: '^[A-Za-z\\-\\s\\.]+$',
      'data-validationMsg':
        'Valid names can contain letters, a hyphen, and a period.',
      maxLength: 30,
      className: styles.noIcon,
      required: true,
    },
    {
      Component: ThemedInput,
      name: 'personalDetails.middleName',
      label: 'Middle Name',
      type: 'text',
      pattern: '^[A-Za-z\\-\\s\\.]*$',
      'data-validationMsg':
        'Valid names can contain letters, a hyphen, and a period.',
      maxLength: 30,
      className: styles.noIcon,
    },
    {
      Component: ThemedInput,
      name: 'personalDetails.lastName',
      label: 'Last Name',
      type: 'text',
      pattern: '^[A-Za-z\\-\\s\\.]+$',
      'data-validationMsg':
        'Valid names can contain letters, a hyphen, and a period.',
      maxLength: 30,
      className: styles.noIcon,
      required: true,
    },
    {
      Component: Dropdown,
      name: 'personalDetails.suffix',
      auto: true,
      source: optionsToDropDown(OptionLists.SuffixOptions),
      key: 'dd-suffix',
      className: styles.noIcon,
    },
    ...buildAddress('personalAddress').map(address => {
      const { name, type } = address;
      const primary = type ? { primary: true } : {};
      return {
        ...address,
        ...primary,
        className: styles.noIcon,
        icon: name.includes('addressLine1') ? 'home' : '',
        required: name.includes('stateCode') || name.includes('postalCode'),
      };
    }),
    {
      Component: ThemedInput,
      name: form => getPhonePathByType(form, 'home'),
      label: 'Home Phone',
      type: 'tel',
      icon: 'phone',
      primary: true,
      onChange: value => formatPhoneNumber('$1-$2-$3', value),
    },
    {
      Component: ThemedInput,
      name: form => getPhonePathByType(form, 'mobile'),
      label: 'Mobile Phone',
      type: 'tel',
      icon: 'smartphone',
      primary: true,
      onChange: value => formatPhoneNumber('$1-$2-$3', value),
    },
  ];

  constructor(props) {
    super(props);

    this.state = {
      errors: {
        form: [],
      },
      submitSuccess: false,
      customer: this.loadCustomerFromProps(props),
    };
  }

  componentWillReceiveProps(nextProps) {
    // if customer is just now being defined, update customer
    // otherwise we don't care about changes because it will overwrite any user changes
    if (!this.props.currentCustomer && nextProps.currentCustomer) {
      this.setState(prevState => ({
        ...prevState,
        customer: this.loadCustomerFromProps(nextProps),
      }));
    }
  }

  loadCustomerFromProps = props => {
    const { currentCustomer } = props;

    let cust = currentCustomer;
    if (cust) {
      let formatedContactDetails = {
        ...cust.contactDetails,
        phoneNumbers: ((cust.contactDetails || {}).phoneNumbers || []
        ).map(phone => ({
          ...phone,
          number: formatPhoneNumber('$1-$2-$3', phone.number),
        })),
      };
      cust = {
        ...cust,
        contactDetails: fillWithDefaults({
          containingObject: formatedContactDetails,
          fillType: 'phone',
          propName: 'phoneNumbers',
          typeList: ['Home', 'Mobile'],
        }),
      };
    }

    return cust;
  };

  handleSubmit = ({ form }, onCompleteHandler) => {
    // save changes
    this.setState(prevState => ({
      ...prevState,
      customer: form,
      submitSuccess: false,
      errors: {
        ...prevState.errors,
        form: [],
      },
    }));

    // remove credentials object
    // eslint-disable-next-line no-unused-vars
    const { organization, credentials, ...rest } = form;
    // remove any phone numbers with empty values
    const sentPhoneNumbers = rest.contactDetails.phoneNumbers
      .filter(
        phone =>
          phone.number &&
          phone.number.length &&
          (phone.type === 'Home' || phone.type === 'Mobile')
      )
      .map(phone => ({
        ...phone,
        number: scrubNumber(phone.number),
      }));
    const requestBody = {
      ...rest,
      contactDetails: {
        ...rest.contactDetails,
        phoneNumbers: sentPhoneNumbers,
      },
      summaryDetails: null
    };

    return this.props.actions.customer.put(requestBody).then(({ payload }) => {
      const { resp, json } = payload;
      if (resp.ok) {
        // show successMessage
        this.setState(prevState => ({
          ...prevState,
          submitSuccess: true,
        }));
        // pass newest customer info to form
        return onCompleteHandler(false, json);
      } else if (resp.status === 500) {
        return this.context.activateServiceDialog();
      } else {
        // get error message
        this.setState(prevState => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            form: [json.message],
          },
        }));

        // parse detailed errors
        let errors =
          json && json.detailedErrors
            ? json.detailedErrors.map(error => ({
                ...error,
                propertyName: normalizePropertyPath(error.propertyName),
              }))
            : [];

        // parse phone errors
        if (errors.length) {
          errors = errors.map(error => {
            if (
              error.propertyName
                .toLowerCase()
                .includes('contactDetails.phoneNumbers'.toLowerCase())
            ) {
              var errorIndex = error.propertyName.match(/\[(.*?)\]/)[1];
              var phoneWithError = sentPhoneNumbers[errorIndex];
              var phoneIndex = form.contactDetails.phoneNumbers.findIndex(
                phone => phone.type === phoneWithError.type
              );
              return {
                ...error,
                propertyName: `contactDetails.phoneNumbers[${phoneIndex}].number`,
              };
            }
            return error;
          });
        }

        return onCompleteHandler(json.message, errors);
      }
    });
  };

  render() {
    const { submitSuccess, errors, customer } = this.state;
    const errorsObj = {
      success: submitSuccess,
      errors: submitSuccess
        ? ['Changes were saved successfully.']
        : errors.form,
    };
    return (
      <CardText>
        <Form
          initState={customer}
          formInputs={this.formInputs}
          formSubmit={this.handleSubmit}
          formErrors={errorsObj}
          warnOnExit
          preventSubmit
          listenToState
        />
      </CardText>
    );
  }
}



// WEBPACK FOOTER //
// ./src/App/account/components/MembershipInfo/PersonalEdit/index.js